import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, X, XCircle } from 'lucide-react';

const NotificationContext = createContext();
const NOTIFICATION_EVENT = 'blupit:notification';

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

const notificationMeta = {
  success: {
    title: 'Acción confirmada',
    icon: CheckCircle2,
    iconColor: 'text-[#20b67a]',
    iconBackground: 'bg-[#e4f8f0]',
    progress: 'bg-[#20b67a]'
  },
  error: {
    title: 'Ocurrió un error',
    icon: XCircle,
    iconColor: 'text-[#f05260]',
    iconBackground: 'bg-[#ffebee]',
    progress: 'bg-[#f05260]'
  },
  info: {
    title: 'Información',
    icon: AlertCircle,
    iconColor: 'text-[#5b43e6]',
    iconBackground: 'bg-[#eeeaff]',
    progress: 'bg-[#5b43e6]'
  }
};

const classifyAlert = (message) => {
  const value = String(message || '').toLowerCase();
  return /(éxito|exitos|correctamente|guardad|cread|actualizad|eliminad|enviad|descargad|completad|✅)/.test(value)
    ? 'success'
    : 'error';
};

if (typeof window !== 'undefined' && !window.__blupitAlertBridgeInstalled) {
  window.__blupitAlertBridgeInstalled = true;
  window.__blupitNotificationQueue = window.__blupitNotificationQueue || [];
  window.__blupitOriginalAlert = window.alert;
  window.alert = (message) => {
    const detail = {
      type: classifyAlert(message),
      description: String(message || '')
    };
    if (window.__blupitNotificationReady) {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail }));
    } else {
      window.__blupitNotificationQueue.push(detail);
    }
  };
}

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const confirmationResolver = useRef(null);
  const idSequence = useRef(0);
  const timers = useRef(new Map());

  const removeNotification = useCallback((id) => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }, []);

  const showNotification = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const normalizedType = type === 'success' ? 'success' : type === 'error' || type === 'warning' || type === 'destructive' ? 'error' : 'info';
    const payload = typeof message === 'object' && message !== null
      ? message
      : { description: String(message || '') };
    const id = options.id || payload.id || `${Date.now()}-${++idSequence.current}`;
    const notification = {
      id,
      type: normalizedType,
      title: options.title || payload.title || notificationMeta[normalizedType].title,
      description: options.description || payload.description || payload.message || '',
      duration: Number.isFinite(duration) ? duration : 5000
    };

    // Updates from the same process replace its row instead of adding cards.
    setNotifications((current) => {
      const index = current.findIndex((item) => item.id === id);
      return index < 0 ? [...current, notification] : current.map((item) => item.id === id ? notification : item);
    });
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    if (notification.duration > 0) {
      timers.current.set(id, window.setTimeout(() => removeNotification(id), notification.duration));
    }
    return id;
  }, [removeNotification]);

  const success = useCallback((message, duration) => showNotification(message, 'success', duration), [showNotification]);
  const error = useCallback((message, duration) => showNotification(message, 'error', duration), [showNotification]);
  const warning = useCallback((message, duration) => showNotification(message, 'error', duration), [showNotification]);
  const info = useCallback((message, duration) => showNotification(message, 'info', duration), [showNotification]);

  const confirmAction = useCallback((messageOrOptions) => {
    const options = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? messageOrOptions
      : { description: String(messageOrOptions || 'Los cambios se aplicarán de inmediato.') };

    if (confirmationResolver.current) confirmationResolver.current(false);
    return new Promise((resolve) => {
      confirmationResolver.current = resolve;
      setConfirmation({
        title: options.title || '¿Confirmar acción?',
        description: options.description || options.message || 'Los cambios se aplicarán de inmediato.',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar'
      });
    });
  }, []);

  const resolveConfirmation = useCallback((accepted) => {
    confirmationResolver.current?.(accepted);
    confirmationResolver.current = null;
    setConfirmation(null);
  }, []);

  useEffect(() => {
    const handleGlobalNotification = (event) => {
      const detail = event.detail || {};
      if (detail.action === 'dismiss') {
        if (detail.id) removeNotification(detail.id);
        else {
          timers.current.forEach((timer) => window.clearTimeout(timer));
          timers.current.clear();
          setNotifications([]);
        }
        return;
      }
      showNotification(
        { id: detail.id, title: detail.title, description: detail.description || detail.message },
        detail.type || detail.variant || 'info',
        detail.duration ?? 5000
      );
    };
    window.addEventListener(NOTIFICATION_EVENT, handleGlobalNotification);
    window.__blupitNotificationReady = true;
    const pendingNotifications = window.__blupitNotificationQueue.splice(0);
    pendingNotifications.forEach((detail) => handleGlobalNotification({ detail }));
    window.appConfirm = confirmAction;

    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, handleGlobalNotification);
      window.__blupitNotificationReady = false;
      if (window.appConfirm === confirmAction) delete window.appConfirm;
      if (confirmationResolver.current) confirmationResolver.current(false);
      timers.current.forEach((timer) => window.clearTimeout(timer));
      timers.current.clear();
    };
  }, [confirmAction, removeNotification, showNotification]);

  return (
    <NotificationContext.Provider value={{ success, error, warning, info, showNotification, confirm: confirmAction }}>
      {children}
      <NotificationContainer
        confirmation={confirmation}
        notifications={notifications}
        onConfirm={resolveConfirmation}
        onRemove={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = ({ confirmation, notifications, onConfirm, onRemove }) => (
  <div
    className="pointer-events-none fixed right-3 top-3 z-[300] flex max-h-[min(360px,calc(100dvh-2rem))] w-[calc(100vw-1.5rem)] max-w-[360px] flex-col gap-2 sm:right-4 sm:top-4"
    aria-live="polite"
    aria-label="Notificaciones"
  >
    {confirmation && (
      <ConfirmationNotification confirmation={confirmation} onResolve={onConfirm} />
    )}
    {notifications.length > 0 && <div className="pointer-events-auto flex min-h-0 flex-col overflow-hidden rounded-xl border border-[#e1e5ed] bg-white shadow-[0_4px_18px_rgba(23,32,51,.09)]">
      {notifications.length > 1 && <div className="flex shrink-0 items-center justify-between border-b border-[#edf0f4] px-3 py-1.5 text-[11px] font-medium text-[#758096]">
        <span>{notifications.length} notificaciones</span>
      </div>}
      <div className="min-h-0 max-h-[220px] overflow-y-auto overscroll-contain divide-y divide-[#edf0f4]">
      {notifications.map((notification) => (
      <StatusNotification
        key={notification.id}
        notification={notification}
        onClose={() => onRemove(notification.id)}
      />
      ))}
      </div>
    </div>}
  </div>
);

const CardShell = ({ children, onClose }) => (
  <section className="pointer-events-auto shrink-0 overflow-hidden rounded-xl border border-[#d8d0ff] bg-white shadow-[0_4px_18px_rgba(23,32,51,.1)] animate-in slide-in-from-top-2 fade-in duration-200">
    <div className="relative p-3">
      {children}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-[#7a8397] transition hover:bg-[#efecff] hover:text-[#5b43e6] focus:outline-none focus:ring-2 focus:ring-[#d8d0ff]"
        aria-label="Cerrar notificación"
      >
        <X size={15} strokeWidth={1.8} />
      </button>
    </div>
  </section>
);

const ConfirmationNotification = ({ confirmation, onResolve }) => (
  <CardShell onClose={() => onResolve(false)}>
    <div className="flex items-start gap-2.5 pr-7">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#eeeaff] text-[#5b43e6]">
        <AlertCircle size={17} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 pt-0.5">
        <h2 className="text-[13px] font-semibold leading-5 text-[#172033]">{confirmation.title}</h2>
        <p className="mt-0.5 max-h-[min(120px,25dvh)] overflow-y-auto whitespace-pre-line break-words text-[12px] leading-[18px] text-[#758096]">{confirmation.description}</p>
      </div>
    </div>
    <div className="mt-2.5 flex justify-end gap-2 border-t border-[#edf0f4] pt-2.5">
      <button
        type="button"
        onClick={() => onResolve(false)}
        className="h-8 rounded-lg border border-[#dfe4ed] bg-white px-3 text-[12px] text-[#3b455b] transition hover:border-[#cfc7ff] hover:text-[#5b43e6] focus:outline-none focus:ring-2 focus:ring-[#e9e5ff]"
      >
        {confirmation.cancelText}
      </button>
      <button
        type="button"
        onClick={() => onResolve(true)}
        className="flex h-8 items-center justify-center gap-1.5 rounded-lg bg-[#5b43e6] px-3 text-[12px] text-white transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#cfc7ff]"
      >
        <Check size={14} />
        {confirmation.confirmText}
      </button>
    </div>
  </CardShell>
);

const StatusNotification = ({ notification, onClose }) => {
  const meta = notificationMeta[notification.type] || notificationMeta.info;
  const Icon = meta.icon;
  return (
    <section className="relative px-3 py-2.5">
      <div className="flex items-start gap-2 pr-6">
        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${meta.iconBackground} ${meta.iconColor}`}>
          <Icon size={15} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 pt-0.5">
          <h2 className="text-[12px] font-medium leading-[18px] text-[#172033]">{notification.title}</h2>
          {notification.description && (
            <p className="mt-0.5 whitespace-pre-line break-words text-[11px] leading-4 text-[#758096]">{notification.description}</p>
          )}
        </div>
      </div>
      <button type="button" onClick={onClose} aria-label="Cerrar notificación" className="absolute right-1.5 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-[#7a8397] hover:bg-[#efecff] hover:text-[#5b43e6] focus:outline-none focus:ring-2 focus:ring-[#d8d0ff]"><X size={14} /></button>
      {notification.duration > 0 && (
        <span
          className={`absolute bottom-0 left-3 right-3 h-[2px] origin-left rounded-full ${meta.progress}`}
          style={{ animation: `notification-progress ${notification.duration}ms linear forwards` }}
        />
      )}
    </section>
  );
};

export const dispatchGlobalNotification = (detail) => {
  if (typeof window === 'undefined') return null;
  window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail }));
  return detail?.id || null;
};
